import styled from "@emotion/styled";
import { aprToApy } from "@stepfinance/common";
import {
  darkGrey2,
  greenTag,
  lightGreen,
  lightGrey2,
  mediaQueries,
  TagInfo,
  TagInfoContainer,
  useViewportWidth,
  white,
} from "@stepfinance/react";
import type { MenuProps } from "antd";
import { Menu } from "antd";
import type { MenuInfo } from "rc-menu/lib/interface";
import React, { useCallback } from "react";

import {
  AccountsIcon,
  DashboardIcon,
  DiscordIcon,
  DocsIcon,
  HistoryIcon,
  MediumIcon,
  NFTGalleryIcon,
  ReferralsIcon,
  StakingIcon,
  StoreIcon,
  SubstackIcon,
  SwapIcon,
  TwitterIcon,
} from "../../components/Icons";
import RedirectLink from "../../components/RedirectLink/RedirectLink";
import { IconLink } from "../../components/shared";
import { useAccountsContext } from "../../contexts/accounts";
import { useDexContext } from "../../contexts/dex";
import { useLanguageContext } from "../../contexts/language/language";
import { useXStepContext } from "../../contexts/xStep";
import { useCalculatePriceForDisplayCurrency } from "../../hooks";
import { formatPct } from "../../utils/utils";

interface MenuItemProps {
  onItemSelect: (item: MenuInfo) => void;
  selectedMenuKey: string[] | undefined;
}

export const MenuItems: React.FC<MenuItemProps> = ({ onItemSelect, selectedMenuKey }) => {
  const { currentViewport } = useViewportWidth();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const { totalClosableAccountsRent } = useDexContext();
  const { apr } = useXStepContext();
  const { accountRent } = useAccountsContext();

  const isMobile = currentViewport === "Mobile";

  const apyDisplay = apr > 0 ? formatPct.format(aprToApy(apr) / 100) : undefined;

  const {
    currentLanguageData: {
      common,
      pages: { allPages, nftGallery, transactionHistory, accountTools, stepStaking, dashboard },
    },
  } = useLanguageContext();

  const getTotalRent = useCallback(() => {
    return totalClosableAccountsRent + accountRent;
  }, [totalClosableAccountsRent, accountRent]);

  const topMenuItems: MenuProps["items"] = [
    {
      label: dashboard.title,
      icon: <DashboardIcon />,
      key: "dashboard",
    },
    {
      label: common.swap,
      icon: <SwapIcon />,
      key: "swap",
    },
    {
      label: (
        <MultiElementContainer>
          {transactionHistory.title}
          <TagInfoContainer>
            <TagInfo tagType="text" uppercase={false} useSpaceMono={false}>
              New!
            </TagInfo>
          </TagInfoContainer>
        </MultiElementContainer>
      ),
      icon: <HistoryIcon />,
      key: "history/latest",
    },
    {
      label: nftGallery.title,
      icon: <NFTGalleryIcon />,
      key: "nft",
    },
    {
      icon: <ReferralsIcon />,
      key: "referrals",
      label: "Referrals",
    },
    {
      icon: <AccountsIcon />,
      key: "account-tools",
      label: (
        <MultiElementContainer>
          {accountTools.title}
          {getTotalRent() > 0 && (
            <TagInfoContainer>
              <TagInfo performanceNumber={getTotalRent()} tagType="performance">
                {calculatePriceForDisplayCurrency(getTotalRent(), { hideable: false })}
              </TagInfo>
            </TagInfoContainer>
          )}
        </MultiElementContainer>
      ),
    },
    {
      label: (
        <MultiElementContainer>
          <span>{stepStaking.title}</span>
          {apyDisplay && (
            <TagInfo tagColorOverride={greenTag} tagType="performance">
              {apyDisplay}
            </TagInfo>
          )}
        </MultiElementContainer>
      ),
      icon: <StakingIcon />,
      key: "stake",
    },
  ];

  const bottomMenuItems: MenuProps["items"] = [
    {
      label: "Reward Options",
      icon: <HistoryIcon />,
      key: "reward-option-stats",
    },
    {
      label: (
        <RedirectLink href="https://analytics.step.finance/">
          <TextLink>{allPages.sideBar.analytics}</TextLink>
        </RedirectLink>
      ),
      icon: <DashboardIcon />,
      key: "analytics outbound",
    },
    {
      key: "docs outbound",
      icon: <DocsIcon />,
      label: (
        <RedirectLink href="https://docs.step.finance/">
          <TextLink>{allPages.sideBar.documentation}</TextLink>
        </RedirectLink>
      ),
    },
  ];

  return (
    <MenuContainer isMobile={isMobile}>
      <NavSection
        isMobile={isMobile}
        inlineIndent={13}
        theme="dark"
        mode="inline"
        onClick={onItemSelect}
        selectedKeys={selectedMenuKey}
        disabledOverflow
        items={topMenuItems}
      />
      <div>
        <NavSection
          isMobile={isMobile}
          inlineIndent={13}
          theme="dark"
          mode="inline"
          onClick={onItemSelect}
          selectedKeys={selectedMenuKey}
          disabledOverflow
          items={bottomMenuItems}
        />
        <LinkNavSection isMobile={isMobile}>
          <IconLink href="https://twitter.com/StepFinance_" color={white}>
            <TwitterIcon height={20} />
          </IconLink>
          <IconLink href="https://discord.gg/Pab8wcH5Yt" color={white}>
            <DiscordIcon height={20} />
          </IconLink>
          <IconLink href="https://stepfinance.medium.com/" color={white}>
            <MediumIcon height={20} />
          </IconLink>
          <IconLink href="https://stepdata.substack.com/" color={white}>
            <SubstackIcon height={18} />
          </IconLink>
          <IconLink href="https://store.step.finance/" color={white}>
            <StoreIcon height={20} />
          </IconLink>
        </LinkNavSection>
      </div>
    </MenuContainer>
  );
};

const MenuContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: ${({ isMobile }) => (isMobile ? "100%" : "calc(100% - 50px)")};
`;

const NavSection = styled(Menu)<{ isMobile?: boolean }>`
  border: 0;
  background: unset;

  ${mediaQueries[4]} {
    padding: 0;

    .ant-menu-item-group {
      padding-bottom: 50px;
    }

    li.ant-menu-item.ant-menu-item-disabled {
      padding-left: 13px;
    }
  }

  .ant-menu-item-group {
    padding-bottom: 50px;
    display: list-item;
    transition: border-color 0.3s, background 0.3s, padding 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
    overflow: hidden;
    text-overflow: ellipsis;

    .ant-menu-item-group-title {
      color: ${lightGrey2};
      font-weight: normal;
      font-size: 10px;
      line-height: 22px;
      padding: 0 0 16px 0;
    }
  }

  .ant-menu-submenu {
    .ant-menu-submenu-title {
      height: 32px;
      margin: 0 0 10px 0;
      padding: 0px 10px;

      font-weight: 700;

      .ant-menu-submenu-arrow {
        color: ${white} !important;

        transition: transform none !important;
        transform: none;
      }

      &:hover,
      &:focus,
      &:active {
        color: ${white};
      }
    }
  }

  .ant-menu-submenu-selected {
    color: ${white};
  }

  .ant-menu-sub {
    .ant-menu-item {
      padding-left: 10px !important;
    }
    background: unset !important;
  }

  .ant-menu-item {
    margin: 0;
    color: ${white};
    background: unset;
    transition: color 0.3s;
    height: 40px;
    margin-bottom: 10px !important;
    padding: 4px 10px;
    border-radius: 8px;

    svg {
      transition: color 0.3s;
      height: 20px;
      width: 20px;
    }

    .ant-menu-title-content {
      padding-left: 14px;
      font-weight: bold;
    }

    &.ant-menu-item-selected {
      background: ${({ isMobile }) => (!isMobile ? darkGrey2 : "unset")};
      color: ${lightGreen};
    }

    &:hover,
    &:focus,
    &:active {
      &:not(.ant-menu-item-selected) {
        color: ${white};
        ${({ isMobile }) => (!isMobile ? `background: ${darkGrey2};` : "")}
      }
    }

    &::after {
      display: none;
    }

    &.ant-menu-item-active {
      .ant-menu-title-content {
        a:hover {
          color: ${white};
        }
      }
    }
  }
`;

const LinkNavSection = styled.div<{ isMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 4px 0px 6px 5px;
  a {
    padding: 8px;
    border-radius: 8px;
  }
  a:hover,
  a:active {
    color: ${white};
    ${({ isMobile }) => (!isMobile ? `background: ${darkGrey2};` : "")}
  }
`;

const MultiElementContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TextLink = styled.span`
  color: ${white};
`;
